import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Pencil, Trash2 } from 'lucide-react'
import { getCharacterType, getCharacterTypeStyle } from '@/utils/characterUtils'
import type { Character, Member } from '@/types'

interface CharacterDetailsSheetProps {
  character: Character
  open: boolean
  onOpenChange: (open: boolean) => void
  membersByUserId: Map<string, Member>
  gameConfig?: any
  // Acoes de quem pode gerenciar o personagem (dono/criador/mestre).
  // Vivem AQUI, nao em menu "..." na linha da lista: o trigger do dropdown
  // abre no pointerdown e disparava durante a rolagem no celular.
  canEdit?: boolean
  onEdit?: () => void
  onDelete?: () => void
}

export function CharacterDetailsSheet({
  character,
  open,
  onOpenChange,
  membersByUserId,
  gameConfig,
  canEdit = false,
  onEdit,
  onDelete
}: CharacterDetailsSheetProps) {
  const characterType = getCharacterType(character, membersByUserId)
  const typeStyle = getCharacterTypeStyle(characterType)
  const TypeIcon = typeStyle.icon
  
  // Buscar nome do membro que criou o personagem
  const member = membersByUserId.get(character.userId)
  const memberName = member?.userFullName || 'Desconhecido'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto p-0">
        <div className="p-6">
          <SheetHeader>
            <div className="flex items-start gap-4">
              {character.imageUrl && (
                <div className="flex-shrink-0">
                  <img 
                    src={character.imageUrl} 
                    alt={character.name}
                    className="w-24 h-24 object-cover rounded-lg border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <SheetTitle className="text-2xl font-bold">{character.name}</SheetTitle>
                  <Badge 
                    variant="outline" 
                    className={`${typeStyle.badge} text-xs px-2 py-1 flex items-center gap-1`}
                  >
                    <TypeIcon className={`h-3 w-3 ${typeStyle.iconColor}`} />
                    {typeStyle.label}
                  </Badge>
                </div>
                <SheetDescription className="text-base">
                  {character.className}
                </SheetDescription>
                <div className="flex items-center gap-4 mt-2">
                  <Badge variant="default" className="bg-primary/10 text-primary border-primary/20">
                    Nível {character.level}
                  </Badge>
                  <Badge variant="outline">
                    {character.points} pontos
                  </Badge>
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-6 mt-6">
          {/* Informações do Criador */}
          <div>
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
              Criado por
            </Label>
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  {memberName.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{memberName}</span>
            </div>
          </div>

          {/* Descrição */}
          {character.description && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Descrição
              </Label>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {character.description}
              </p>
            </div>
          )}

          {/* Pontos de Vida */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Pontos de Vida
              </Label>
              <span className="text-sm font-bold">
                {character.hpCurrent}/{character.hpTotal}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-red-500 to-red-400 h-full rounded-full transition-all duration-300" 
                style={{ width: `${Math.max(5, (character.hpCurrent / character.hpTotal) * 100)}%` }}
              />
            </div>
          </div>

          {/* Atributos */}
          <div>
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 block">
              Atributos
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Object.entries(character.attributes).map(([key, value]) => {
                const attributeName = gameConfig?.attributes?.find((attr: any) => attr.id === key)?.name || key;
                
                return (
                  <div key={key} className="bg-muted/30 rounded-lg p-3 text-center border">
                    <div className="text-2xl font-bold text-primary mb-1">{value}</div>
                    <div className="text-xs text-muted-foreground font-medium" title={attributeName}>
                      {attributeName}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ações de gerenciamento (dono/criador/mestre) */}
          {canEdit && (
            <div className="mt-6 flex gap-2 border-t border-border pt-4">
              <Button
                variant="outline"
                className="flex-1 font-bold uppercase tracking-wide"
                onClick={onEdit}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </Button>
              <Button
                variant="outline"
                className="flex-1 font-bold uppercase tracking-wide text-error hover:bg-error-bg hover:text-error"
                onClick={onDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </Button>
            </div>
          )}
        </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
